import { refreshMunicipalPropertyManagement } from "@/domain/municipal-property/lib/refresh-municipal-property-management";
import { useMovedToTrashToast } from "@/domain/trash/hooks/use-moved-to-trash-toast";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { ClearMunicipalPropertyInventorySelectionMutation, RemoveMunicipalPropertyRegisterMutation, RemoveMunicipalPropertyYearMutation } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { QueryClient, useMutation, UseMutationResult, useQueryClient } from "@tanstack/react-query";

import { clearMunicipalPropertyInventorySelectionMutationDocument } from "./clear-municipal-property-inventory-selection-mutation.gql";
import { REMOVE_MUNICIPAL_PROPERTY_REGISTER_MUTATION_DOCUMENT } from "./remove-municipal-property-register-mutation.gql";
import { REMOVE_MUNICIPAL_PROPERTY_YEAR_MUTATION_DOCUMENT } from "./remove-municipal-property-year-mutation.gql";

interface RemoveContentResult {
  readonly removeRegister: UseMutationResult<RemoveMunicipalPropertyRegisterMutation, Error, string>;
  readonly removeYear: UseMutationResult<RemoveMunicipalPropertyYearMutation, Error, string>;
}

export function useRemoveMunicipalPropertyContent({ canRestore = false }: { canRestore?: boolean } = {}): RemoveContentResult {
  const queryClient: QueryClient = useQueryClient();
  const { currentWorkspace }: ReturnType<typeof useCurrentWorkspaceContext> = useCurrentWorkspaceContext();
  const notifyMovedToTrash: ReturnType<typeof useMovedToTrashToast> = useMovedToTrashToast();
  const removeRegister: RemoveContentResult["removeRegister"] = useMutation({
    mutationFn: (id: string): Promise<RemoveMunicipalPropertyRegisterMutation> => createOperationClient("RemoveMunicipalPropertyRegister")
      .request(REMOVE_MUNICIPAL_PROPERTY_REGISTER_MUTATION_DOCUMENT, { input: { id } }),
    onSuccess: async (_result: RemoveMunicipalPropertyRegisterMutation, id: string): Promise<void> => {
      await refreshMunicipalPropertyManagement({ queryClient, scope: { workspaceId: currentWorkspace.id, propertyIdList: [] } });
      notifyMovedToTrash({ entityType: "municipalPropertyRegister", idList: [id], canRestore });
    },
  });
  const removeYear: RemoveContentResult["removeYear"] = useMutation({
    mutationFn: (id: string): Promise<RemoveMunicipalPropertyYearMutation> =>
      createOperationClient("RemoveMunicipalPropertyYear").request(REMOVE_MUNICIPAL_PROPERTY_YEAR_MUTATION_DOCUMENT, { input: { id } }),
    onSuccess: async (_result: RemoveMunicipalPropertyYearMutation, id: string): Promise<void> => {
      await refreshMunicipalPropertyManagement({ queryClient, scope: { workspaceId: currentWorkspace.id, propertyIdList: [] } });
      notifyMovedToTrash({ entityType: "municipalPropertyYear", idList: [id], canRestore });
    },
  });
  return { removeRegister, removeYear };
}

export interface ClearInventorySelectionRequest {
  inventoryId: string;
  workspaceId: string;
}

export function useClearMunicipalPropertyInventorySelection(): UseMutationResult<ClearMunicipalPropertyInventorySelectionMutation, Error, ClearInventorySelectionRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ inventoryId }: ClearInventorySelectionRequest): Promise<ClearMunicipalPropertyInventorySelectionMutation> =>
      createOperationClient("ClearMunicipalPropertyInventorySelection").request(clearMunicipalPropertyInventorySelectionMutationDocument, { input: { inventoryId } }),
    onSuccess: async (_result, { workspaceId }): Promise<void> => {
      await refreshMunicipalPropertyManagement({ queryClient, scope: { workspaceId, propertyIdList: [] } }).catch(() => undefined);
    },
  });
}
